import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  IconButton,
  ButtonBase,
  useScrollTrigger
} from '@mui/material';
import { Home, LibraryMusic } from '@mui/icons-material';
import PodcastCoverArt from '@components/shared/PodcastCoverArt';
import { useNavigationController } from '@controllers/useNavigationController';

export default function AppHeader() {
  const {
    feedMetadata,
    isHome,
    isAllEpisodes,
    goHome,
    goToAllEpisodes
  } = useNavigationController();

  // Elevate app bar on scroll
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0
  });

  return (
    <AppBar
      position="sticky"
      elevation={trigger ? 4 : 0}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        pt: { xs: 'env(safe-area-inset-top)', sm: 0 }
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2 } }}>
        <Toolbar disableGutters sx={{ gap: { xs: 1, sm: 2 }, minHeight: { xs: 64, sm: 70 } }}>
          {/* Logo/Branding */}
          <ButtonBase
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: { xs: 1, sm: 1.5 },
              flexGrow: 1,
              minWidth: 0,
              borderRadius: 1,
              textAlign: 'left',
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 3
              }
            }}
            onClick={goHome}
            aria-label="Go to podcast home"
          >
            <PodcastCoverArt
              imageUrl={feedMetadata?.image}
              alt={feedMetadata?.title}
              size={40}
              variant="rounded"
              loading="eager"
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                component="div"
                noWrap
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '0.95rem', sm: '1.25rem' },
                  lineHeight: 1.2
                }}
              >
                {feedMetadata?.title || 'So You Can Hear Me When I\'m Gone'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  fontSize: '0.75rem'
                }}
              >
                Family Stories Podcast
              </Typography>
            </Box>
          </ButtonBase>

          {/* Navigation */}
          <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 1 } }}>
            <IconButton
              color={isHome ? 'primary' : 'default'}
              onClick={goHome}
              aria-label="Home"
              aria-current={isHome ? 'page' : undefined}
              title="Home"
              sx={{
                bgcolor: isHome ? 'primary.main' : 'transparent',
                color: isHome ? 'primary.contrastText' : 'text.secondary',
                p: 1,
                '&:hover': {
                  bgcolor: isHome ? 'primary.dark' : 'action.hover'
                }
              }}
            >
              <Home />
            </IconButton>
            <IconButton
              color={isAllEpisodes ? 'primary' : 'default'}
              onClick={goToAllEpisodes}
              aria-label="All episodes"
              aria-current={isAllEpisodes ? 'page' : undefined}
              title="All episodes"
              sx={{
                bgcolor: isAllEpisodes ? 'primary.main' : 'transparent',
                color: isAllEpisodes ? 'primary.contrastText' : 'text.secondary',
                p: 1,
                '&:hover': {
                  bgcolor: isAllEpisodes ? 'primary.dark' : 'action.hover'
                }
              }}
            >
              <LibraryMusic />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
